/**
 * Events Service
 * Handles all event-related database operations
 */

import {
  supabase,
  type PaginatedResponse,
  type SortOption,
} from './supabaseClient';
import { Database } from '@/types/supabase';
import { NotFoundError, DatabaseError } from '@/lib/errors';
import { PAGINATION } from '@/constants';
import {
  Event,
  EventInsert,
  EventUpdate,
  EventWithSessions,
  EventWithStatistics,
  EventStatus,
  EventStatistics,
} from '@/types/event.types';

export type EventFilters = {
  search?: string;
  status?: EventStatus | 'all';
  startDateFrom?: string;
  startDateTo?: string;
};

// Type helpers for database operations
type EventRow = Database['public']['Tables']['events']['Row'];

/**
 * Get paginated events with optional filters and sorting
 * @param page - Page number (1-indexed)
 * @param pageSize - Number of items per page
 * @param filters - Optional filters (status, date range, search)
 * @param sort - Optional sort option
 * @throws {DatabaseError} If the database query fails
 */
export async function getEventsPaginated(
  page: number = 1,
  pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE,
  filters?: EventFilters,
  sort?: SortOption,
): Promise<PaginatedResponse<Event>> {
  try {
    let query = supabase.from('events').select('*', { count: 'exact' });

    // Apply filters
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.startDateFrom) {
      query = query.gte('start_date', filters.startDateFrom);
    }
    if (filters?.startDateTo) {
      query = query.lte('end_date', filters.startDateTo);
    }
    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,code.ilike.%${filters.search}%,location.ilike.%${filters.search}%`,
      );
    }

    // Apply sorting
    if (sort) {
      query = query.order(sort.field, { ascending: sort.direction === 'asc' });
    } else {
      // Default sort: most recent first
      query = query.order('created_at', { ascending: false });
    }

    // Apply pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      data: (data as EventRow[]) || [],
      count: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  } catch (error) {
    throw new DatabaseError('Failed to fetch events', error);
  }
}

/**
 * Get all events (without pagination)
 * @throws {DatabaseError} If the database query fails
 */
export async function getAllEvents(): Promise<Event[]> {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .order('start_date', { ascending: false });

    if (error) throw error;
    return (data as EventRow[]) || [];
  } catch (error) {
    throw new DatabaseError('Failed to fetch all events', error);
  }
}

/**
 * Get a single event by ID
 * @throws {NotFoundError} If event is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getEventById(id: string): Promise<Event | null> {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Event with ID ${id} not found`);
      }
      throw error;
    }

    return data as Event;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch event with ID ${id}`, error);
  }
}

/**
 * Get event with its sessions
 * @throws {NotFoundError} If event is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getEventWithSessions(
  id: string,
): Promise<EventWithSessions> {
  try {
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('*')
      .eq('id', id)
      .single();

    if (eventError) {
      if (eventError.code === 'PGRST116') {
        throw new NotFoundError(`Event with ID ${id} not found`);
      }
      throw eventError;
    }

    const { data: sessions, error: sessionsError } = await supabase
      .from('event_sessions')
      .select('*')
      .eq('event_id', id)
      .order('display_order', { ascending: true });

    if (sessionsError) throw sessionsError;

    return {
      ...(event as Event),
      sessions: sessions || [],
    };
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(
      `Failed to fetch event with sessions for ID ${id}`,
      error,
    );
  }
}

/**
 * Get event with statistics from database function
 * @throws {NotFoundError} If event is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getEventWithStatistics(
  id: string,
): Promise<EventWithStatistics> {
  try {
    const event = await getEventById(id);

    const { data: stats, error: statsError } = await supabase.rpc(
      'get_event_statistics',
      { p_event_id: id },
    );

    if (statsError) throw statsError;

    return {
      ...event!,
      statistics: stats as unknown as EventStatistics,
    };
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(
      `Failed to fetch event statistics for ID ${id}`,
      error,
    );
  }
}

/**
 * Create a new event
 * @throws {DatabaseError} If the database operation fails
 */
export async function createEvent(event: EventInsert): Promise<Event> {
  try {
    const { data, error } = await supabase
      .from('events')
      .insert(event as never)
      .select()
      .single();

    if (error) throw error;
    return data as Event;
  } catch (error) {
    throw new DatabaseError('Failed to create event', error);
  }
}

/**
 * Update an existing event
 * @throws {NotFoundError} If event is not found
 * @throws {DatabaseError} If the database operation fails
 */
export async function updateEvent(
  id: string,
  updates: EventUpdate,
): Promise<Event> {
  try {
    const { data, error } = await supabase
      .from('events')
      .update({ ...updates, updated_at: new Date().toISOString() } as never)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Event with ID ${id} not found`);
      }
      throw error;
    }

    return data as Event;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to update event with ID ${id}`, error);
  }
}

/**
 * Delete an event (hard delete - use with caution)
 * @throws {DatabaseError} If the database operation fails
 */
export async function deleteEvent(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('events').delete().eq('id', id);

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to delete event with ID ${id}`, error);
  }
}

/**
 * Generate next event code (e.g., "EVT-2026-001")
 * @param year - Year for the event code
 * @throws {DatabaseError} If the database operation fails
 */
export async function generateEventCode(year?: number): Promise<string> {
  try {
    const targetYear = year || new Date().getFullYear();

    // Get the latest event for this year
    const { data, error } = await supabase
      .from('events')
      .select('code')
      .ilike('code', `EVT-${targetYear}-%`)
      .order('code', { ascending: false })
      .limit(1);

    if (error) throw error;

    if (!data || data.length === 0) {
      // First event of the year
      return `EVT-${targetYear}-001`;
    }

    // Extract the sequence number and increment
    const lastCode = data[0].code;
    const match = lastCode.match(/EVT-\d{4}-(\d{3})/);

    if (match) {
      const nextSequence = parseInt(match[1], 10) + 1;
      return `EVT-${targetYear}-${nextSequence.toString().padStart(3, '0')}`;
    }

    // Fallback if format is unexpected
    return `EVT-${targetYear}-001`;
  } catch (error) {
    throw new DatabaseError('Failed to generate event code', error);
  }
}

/**
 * Transition event status with appropriate timestamp updates
 * @throws {NotFoundError} If event is not found
 * @throws {DatabaseError} If the database operation fails
 */
export async function transitionEventStatus(
  id: string,
  newStatus: EventStatus,
  userId: string,
): Promise<Event> {
  try {
    const updates: EventUpdate = {
      status: newStatus,
      updated_by: userId,
    };

    // Set appropriate timestamp based on status
    const now = new Date().toISOString();
    if (newStatus === 'OPEN') {
      updates.published_at = now;
    } else if (newStatus === 'CLOSED') {
      updates.closed_at = now;
    } else if (newStatus === 'COMPLETED') {
      updates.completed_at = now;
    }

    return await updateEvent(id, updates);
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(
      `Failed to transition event status for ID ${id}`,
      error,
    );
  }
}

/**
 * Upload event flyer to Supabase Storage
 * Automatically deletes old flyer if one exists
 * @param eventId - Event ID
 * @param file - File to upload
 * @param userId - User performing the upload
 * @throws {DatabaseError} If the upload or update fails
 */
export async function uploadEventFlyer(
  eventId: string,
  file: File,
  userId: string,
): Promise<Event> {
  try {
    // Get existing event to check for old flyer
    const existingEvent = await getEventById(eventId);
    const oldFilename = existingEvent?.flyer_filename;

    // Delete old flyer if exists
    if (oldFilename) {
      const oldFilePath = `Flyers/${oldFilename}`;
      const { error: deleteError } = await supabase.storage
        .from('EventAssets')
        .remove([oldFilePath]);

      if (deleteError) {
        console.warn('Failed to delete old flyer:', deleteError);
        // Continue anyway - don't fail the upload
      }
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${eventId}-${Date.now()}.${fileExt}`;
    const filePath = `Flyers/${fileName}`;

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('EventAssets')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false, // Changed to false to prevent conflicts
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw uploadError;
    }

    // Update event with filename
    const updated = await updateEvent(eventId, {
      flyer_filename: fileName,
      updated_by: userId,
    });

    return updated;
  } catch (error) {
    console.error('Error in uploadEventFlyer:', error);
    throw new DatabaseError(
      `Failed to upload flyer for event ID ${eventId}`,
      error,
    );
  }
}

/**
 * Get public URL for event flyer
 * @param filename - Flyer filename stored in database
 * @returns Public URL or null if no filename
 */
export function getEventFlyerUrl(filename: string | null): string | null {
  if (!filename) return null;

  const filePath = `Flyers/${filename}`;
  const { data } = supabase.storage.from('EventAssets').getPublicUrl(filePath);

  return data.publicUrl;
}

/**
 * Delete event flyer from storage
 * @param eventId - Event ID
 * @param filename - Flyer filename
 * @param userId - User performing the delete
 * @throws {DatabaseError} If the delete operation fails
 */
export async function deleteEventFlyer(
  eventId: string,
  filename: string,
  userId: string,
): Promise<Event> {
  try {
    const filePath = `Flyers/${filename}`;

    // Delete from storage
    const { error: deleteError } = await supabase.storage
      .from('EventAssets')
      .remove([filePath]);

    if (deleteError) throw deleteError;

    // Update event to remove filename
    const updated = await updateEvent(eventId, {
      flyer_filename: null,
      updated_by: userId,
    });

    return updated;
  } catch (error) {
    throw new DatabaseError(
      `Failed to delete flyer for event ID ${eventId}`,
      error,
    );
  }
}

// Bank payment QR (public EventAssets bucket, PaymentQR/ folder)

/** Public URL of the event's bank payment QR image */
export function getEventPaymentQrUrl(filename: string | null | undefined): string | null {
  if (!filename) return null;
  const { data } = supabase.storage.from('EventAssets').getPublicUrl(`PaymentQR/${filename}`);
  return data.publicUrl;
}

/** Upload (replacing any previous one) the bank payment QR image */
export async function uploadEventPaymentQr(eventId: string, file: File, userId: string): Promise<Event> {
  try {
    const existing = await getEventById(eventId);
    const oldFilename = (existing as { payment_qr_filename?: string | null } | null)?.payment_qr_filename;
    if (oldFilename) {
      await supabase.storage.from('EventAssets').remove([`PaymentQR/${oldFilename}`]);
    }

    const ext = file.name.split('.').pop() || 'png';
    const fileName = `${eventId}-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('EventAssets')
      .upload(`PaymentQR/${fileName}`, file, { cacheControl: '3600', upsert: false });
    if (uploadError) throw uploadError;

    return await updateEvent(eventId, { payment_qr_filename: fileName, updated_by: userId } as EventUpdate);
  } catch (error) {
    throw new DatabaseError(`Failed to upload payment QR for event ID ${eventId}`, error);
  }
}

export async function deleteEventPaymentQr(eventId: string, filename: string, userId: string): Promise<Event> {
  try {
    const { error } = await supabase.storage.from('EventAssets').remove([`PaymentQR/${filename}`]);
    if (error) throw error;
    return await updateEvent(eventId, { payment_qr_filename: null, updated_by: userId } as EventUpdate);
  } catch (error) {
    throw new DatabaseError(`Failed to delete payment QR for event ID ${eventId}`, error);
  }
}

/**
 * Check if event code is unique
 * @param code - Event code to check
 * @param excludeId - Optional event ID to exclude (for updates)
 * @returns true if code is available, false if taken
 * @throws {DatabaseError} If the database query fails
 */
export async function isEventCodeUnique(
  code: string,
  excludeId?: string,
): Promise<boolean> {
  try {
    // Case-insensitive: public lookup (public_get_event) ignores letter case
    let query = supabase.from('events').select('id').ilike('code', code);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return !data || data.length === 0;
  } catch (error) {
    throw new DatabaseError('Failed to check event code uniqueness', error);
  }
}

/**
 * Get events by status
 * @param status - Event status to filter by
 * @throws {DatabaseError} If the database query fails
 */
export async function getEventsByStatus(status: EventStatus): Promise<Event[]> {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('status', status)
      .order('start_date', { ascending: false });

    if (error) throw error;
    return (data as EventRow[]) || [];
  } catch (error) {
    throw new DatabaseError(
      `Failed to fetch events with status ${status}`,
      error,
    );
  }
}

/**
 * Get upcoming events (start_date >= today, status OPEN or CLOSED)
 * @throws {DatabaseError} If the database query fails
 */
export async function getUpcomingEvents(): Promise<Event[]> {
  try {
    const today = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('events')
      .select('*')
      .gte('start_date', today)
      .in('status', ['OPEN', 'CLOSED'])
      .order('start_date', { ascending: true });

    if (error) throw error;
    return (data as EventRow[]) || [];
  } catch (error) {
    throw new DatabaseError('Failed to fetch upcoming events', error);
  }
}

/**
 * Get events within a date range
 * @param startDate - Range start date (ISO format)
 * @param endDate - Range end date (ISO format)
 * @throws {DatabaseError} If the database query fails
 */
export async function getEventsByDateRange(
  startDate: string,
  endDate: string,
): Promise<Event[]> {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .gte('start_date', startDate)
      .lte('end_date', endDate)
      .order('start_date', { ascending: true });

    if (error) throw error;
    return (data as EventRow[]) || [];
  } catch (error) {
    throw new DatabaseError(
      `Failed to fetch events between ${startDate} and ${endDate}`,
      error,
    );
  }
}
